import { Role, rolesManipulator } from 'auth';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { Formik } from 'formik';
import capitalize from 'lodash/capitalize';
import get from 'lodash/get';
import lowerCase from 'lodash/lowerCase';
import { useCallback, useMemo, useState } from 'react';
import { FormGeneratorOnSubmitParams, ReactFormGenerator } from 'react-form-builder';
import { RRule } from 'rrule';
import Badge from '@/modules/common/components/Badge.tsx';
import FormResultsModalTitle from '@/modules/common/components/FormResultsModalTitle.tsx';
import { useFetchAllRegisters } from '@/modules/common/hooks/useFetchAllRegisters.ts';
import { useUpdateTaskResultMutation } from '@/modules/admin-dashboard/queries/tasks.ts';
import { Avatar, BaseModalProps, ModalContentWrapper, Select, SelectOption, Spinner } from 'ui';
import { useOpen } from 'ui/hooks';
import {
  RegisterResult,
  TaskListWithRelationsResponse,
  TaskResultSchema,
  User,
} from 'compliance-api';

export interface TaskResultInfo {
  task: Pick<
    TaskListWithRelationsResponse,
    'name' | 'description' | 'recurrency_rule' | 'first_due_date' | 'registers'
  >;
  user: User;
  result?: TaskResultSchema;
}

const complianceOptions: SelectOption<RegisterResult>[] = Object.values(RegisterResult).map(
  value => ({
    value,
    label: capitalize(lowerCase(value)),
  }),
);

const PRINT_CLONE_ID = 'task-result-inline-print-clone';
const PRINT_STYLE_ID = 'task-result-inline-print-style';

const canvasToImage = (canvas: HTMLCanvasElement) => {
  const img = document.createElement('img');
  img.src = canvas.toDataURL('image/png');
  img.width = canvas.width;
  img.height = canvas.height;
  img.style.display = 'block';
  img.className = 'task-result-print-canvas-image';
  img.style.width = `${canvas.width}px`;
  img.style.minWidth = `${Math.max(canvas.width, 320)}px`;
  img.style.height = `${canvas.height}px`;
  img.style.maxWidth = 'none';
  img.style.objectFit = 'contain';
  img.style.background = '#fff';
  return img;
};

interface TaskInfoModalProps extends BaseModalProps {
  taskResultInfo: TaskResultInfo;
  onClose: () => void;
  allowPrintForNonAdmin?: boolean;
  isLoading?: boolean;
  errorMessage?: string | null;
}

const TaskResultInfoModal = ({
  taskResultInfo,
  onClose,
  open,
  allowPrintForNonAdmin = false,
  isLoading: isResultLoading = false,
  errorMessage = null,
}: TaskInfoModalProps) => {
  const [editing, turnOnEditing, turnOffEditing] = useOpen();
  const [registersFilter, setRegistersFilter] = useState<number[]>([]);
  const [complianceFilter, setComplianceFilter] = useState<RegisterResult | null>(null);
  const isUserAdmin = rolesManipulator.current_user_role === Role.COMPLIANCE_ADMIN;

  const {
    registers,
    isLoading: isLoadingRegisters,
    isFetchingNextPage: isFetchingRegistersNextPage,
  } = useFetchAllRegisters();

  const { mutateAsync, isLoading } = useUpdateTaskResultMutation();

  const { task, result, user } = taskResultInfo || {};
  const sourceFormItems = get(result, 'form_results.formItems', []);

  const formik = useFormik({
    initialValues: {
      formItems: sourceFormItems,
      form_results: get(result, 'form_results.form_results', []),
    },
    enableReinitialize: true,
    onSubmit: async form_results => {
      if (!taskResultInfo.result?.id) return;
      await mutateAsync({ id: taskResultInfo.result.id, form_results });
      onClose();
    },
  });

  const formItems = useMemo(() => {
    let res = editing ? formik.values.formItems : sourceFormItems;
    if (editing) return res;

    if (complianceFilter) {
      res = res.filter(item => {
        if (!get(item, 'compliance')) return false;
        const answerData = formik.values.form_results.find(({ id }) => get(item, 'id') === id);
        const options = get(item, 'options', []);

        if (!answerData || !get(answerData, 'value') || !options?.length) return false;

        const answersValue = Array.isArray(get(answerData, 'value'))
          ? get(answerData, 'value')
          : [get(answerData, 'value')];

        const targetOption = options.find(option => get(option, 'compliance') === complianceFilter);

        if (!targetOption) return false;

        return answersValue.includes(get(targetOption, 'id'));
      });
    }

    return registersFilter.length
      ? res.filter(
          item => get(item, 'compliance') && registersFilter.includes(get(item, 'registerId', 0)),
        )
      : res;
  }, [
    complianceFilter,
    editing,
    formik.values.formItems,
    formik.values.form_results,
    registersFilter,
    sourceFormItems,
  ]);

  const adaptedQuestionsData = useMemo(
    () =>
      formik.values.form_results.map((item: object) => {
        const isUrlInItem = 'url' in item && typeof item.url === 'string';
        const isValueInItem = 'value' in item;

        let value = get(item, 'value', '');

        if (isUrlInItem && !isValueInItem) {
          value = item.url as string;
        }

        return { ...item, value };
      }),
    [formik.values.form_results],
  );

  const formGeneratorKey = useMemo(
    () =>
      JSON.stringify({
        editing,
        complianceFilter,
        registersFilter,
        itemIds: formItems.map(item => get(item, 'id')),
      }),
    [complianceFilter, editing, formItems, registersFilter],
  );

  const handleToggleRegisterFilter = useCallback((id: number) => {
    setRegistersFilter(filter => {
      const isActive = filter.includes(id);

      return isActive ? filter.filter(idx => idx !== id) : [...filter, id];
    });
  }, []);

  const handleChangeAnswers = useCallback(
    (answers: FormGeneratorOnSubmitParams[]) => {
      if (!editing) return;
      formik.setFieldValue('form_results', answers);
    },
    [editing],
  );

  const handleChangeItems = useCallback(
    (items: FormGeneratorOnSubmitParams[]) => {
      if (!editing) return;
      formik.setFieldValue('formItems', items);
    },
    [editing],
  );

  const handlePrint = useCallback(() => {
    const source = document.getElementById('print-area');
    if (!source || isResultLoading || !result) return;

    document.getElementById(PRINT_CLONE_ID)?.remove();
    document.getElementById(PRINT_STYLE_ID)?.remove();

    const clone = source.cloneNode(true) as HTMLElement;
    clone.id = PRINT_CLONE_ID;
    clone.classList.add('task-result-print-root');

    const originalCanvases = source.querySelectorAll('canvas');
    const clonedCanvases = clone.querySelectorAll('canvas');

    originalCanvases.forEach((originalCanvas, index) => {
      const clonedCanvas = clonedCanvases[index];
      if (!clonedCanvas) return;
      clonedCanvas.replaceWith(canvasToImage(originalCanvas));
    });

    clone.querySelectorAll('*').forEach(element => {
      if (element.tagName === 'IMG' || element.tagName === 'SVG' || element.tagName === 'CANVAS') {
        return;
      }

      const htmlElement = element as HTMLElement;
      htmlElement.style.overflow = 'visible';
      htmlElement.style.maxHeight = 'none';

      if (htmlElement.style.height && htmlElement.style.height !== 'auto') {
        htmlElement.style.height = 'auto';
      }
    });

    clone.querySelectorAll('input[type="checkbox"]').forEach(input => {
      const htmlInput = input as HTMLInputElement;
      const wrapper = htmlInput.closest('.custom-checkbox');
      const radioWrapper = htmlInput.closest('.custom-radio');

      if (!wrapper || radioWrapper) {
        return;
      }

      const marker = document.createElement('span');
      marker.className = 'task-result-print-marker';
      marker.textContent = htmlInput.checked ? '☑' : '☐';

      const label = htmlInput.id
        ? clone.querySelector(`label[for="${CSS.escape(htmlInput.id)}"]`)
        : htmlInput.closest('label');

      if (label) {
        label.prepend(marker);
      } else {
        htmlInput.parentElement?.prepend(marker);
      }

      htmlInput.style.display = 'none';
    });

    clone.querySelectorAll('img[alt="signature"], canvas, .signature-pad').forEach(element => {
      const htmlElement = element as HTMLElement;
      // htmlElement.style.border = '1px solid #94a3b8';
      // htmlElement.style.borderRadius = '8px';
      htmlElement.style.background = '#fff';
    });

    clone.querySelectorAll('.signature-pad, .signature-pad canvas').forEach(element => {
      const htmlElement = element as HTMLElement;
      htmlElement.style.width = '100%';
      htmlElement.style.minWidth = '320px';
      htmlElement.style.maxWidth = '100%';
      htmlElement.style.display = 'block';
    });

    clone.querySelectorAll('.image-upload-container, [class*="border-dashed"]').forEach(element => {
      const htmlElement = element as HTMLElement;
      // htmlElement.style.border = '1px dashed #64748b';
      // htmlElement.style.borderRadius = '8px';
      htmlElement.style.background = 'transparent';
    });

    clone.querySelectorAll('.bg-neutral-100, .bg-gray-100').forEach(element => {
      const htmlElement = element as HTMLElement;
      htmlElement.style.background = 'transparent';
      htmlElement.style.border = '1px solid #cbd5e1';
    });

    formItems
      .filter(item => get(item, 'type') === 'Signature')
      .forEach(item => {
        const signatureValue = adaptedQuestionsData.find(
          answer => get(answer, 'id') === get(item, 'id'),
        )?.value;

        if (typeof signatureValue !== 'string' || !signatureValue) {
          return;
        }

        const signatureRoot = clone.querySelector(
          `[data-anchor="${CSS.escape(String(get(item, 'id')))}"]`,
        ) as HTMLElement | null;

        if (!signatureRoot) {
          return;
        }

        const existingSignatureImage = signatureRoot.querySelector('img[alt="signature"]');
        if (existingSignatureImage) {
          existingSignatureImage.setAttribute('src', signatureValue);
          return;
        }

        const existingSignatureCanvas = signatureRoot.querySelector('canvas');
        if (!existingSignatureCanvas) {
          return;
        }

        const signatureImage = document.createElement('img');
        signatureImage.src = signatureValue;
        signatureImage.alt = 'signature';
        signatureImage.className = 'task-result-print-signature-image';
        signatureImage.style.display = 'block';
        signatureImage.style.width = '100%';
        signatureImage.style.minWidth = '320px';
        signatureImage.style.minHeight = '120px';
        signatureImage.style.objectFit = 'contain';
        signatureImage.style.background = '#fff';
        signatureImage.style.border = '1px solid #94a3b8';
        signatureImage.style.borderRadius = '8px';

        existingSignatureCanvas.replaceWith(signatureImage);
      });

    formItems.forEach(item => {
      const selectedValue = adaptedQuestionsData.find(
        answer => get(answer, 'id') === get(item, 'id'),
      )?.value;

      if (typeof selectedValue !== 'string') {
        return;
      }

      const itemRoot = clone.querySelector(
        `[data-anchor="${CSS.escape(String(get(item, 'id')))}"]`,
      ) as HTMLElement | null;

      const select = itemRoot?.querySelector('select') as HTMLSelectElement | null;

      if (!select) {
        return;
      }

      select.value = selectedValue;
      Array.from(select.options).forEach(option => {
        option.selected = option.value === selectedValue;
      });
    });

    const style = document.createElement('style');
    style.id = PRINT_STYLE_ID;
    style.textContent = `
      @media print {
        @page {
          size: A4 portrait;
          margin: 12mm;
        }

        html,
        body {
          width: 100% !important;
          height: auto !important;
          margin: 0 !important;
          padding: 0 !important;
          overflow: visible !important;
          background: #fff !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }

        body > * {
          display: none !important;
        }

        body > #${PRINT_CLONE_ID} {
          display: block !important;
          position: static !important;
          inset: auto !important;
          width: 100% !important;
          max-width: none !important;
          margin: 0 !important;
          padding: 0 !important;
          overflow: visible !important;
          background: #fff !important;
          color: #000 !important;
          box-sizing: border-box !important;
        }

        body > #${PRINT_CLONE_ID} * {
          visibility: visible !important;
          max-height: none !important;
        }

        body > #${PRINT_CLONE_ID},
        body > #${PRINT_CLONE_ID} div,
        body > #${PRINT_CLONE_ID} section,
        body > #${PRINT_CLONE_ID} article,
        body > #${PRINT_CLONE_ID} table,
        body > #${PRINT_CLONE_ID} tr,
        body > #${PRINT_CLONE_ID} td,
        body > #${PRINT_CLONE_ID} th,
        body > #${PRINT_CLONE_ID} p,
        body > #${PRINT_CLONE_ID} img,
        body > #${PRINT_CLONE_ID} svg,
        body > #${PRINT_CLONE_ID} canvas {
          overflow: visible !important;
          break-inside: auto;
          page-break-inside: auto;
        }

        body > #${PRINT_CLONE_ID} .rfb-item,
        body > #${PRINT_CLONE_ID} .react-form-builder-form > div,
        body > #${PRINT_CLONE_ID} .SortableItem,
        body > #${PRINT_CLONE_ID} .signature-pad,
        body > #${PRINT_CLONE_ID} .signature-pad canvas,
        body > #${PRINT_CLONE_ID} .task-result-print-canvas-image,
        body > #${PRINT_CLONE_ID} .image-upload,
        body > #${PRINT_CLONE_ID} .image-upload-container,
        body > #${PRINT_CLONE_ID} .task-result-print-block {
          break-inside: avoid !important;
          page-break-inside: avoid !important;
        }

        body > #${PRINT_CLONE_ID} .task-result-print-marker {
          display: inline-block !important;
          min-width: 18px !important;
          margin-right: 8px !important;
          color: #0f172a !important;
          font-size: 16px !important;
          font-weight: 700 !important;
          line-height: 1 !important;
          vertical-align: text-top !important;
        }

        body > #${PRINT_CLONE_ID} .custom-control-label::before,
        body > #${PRINT_CLONE_ID} .custom-control-label::after {
          display: none !important;
          content: none !important;
        }

        body > #${PRINT_CLONE_ID} .custom-control-label {
          padding-left: 0 !important;
          margin-left: 0 !important;
        }

        body > #${PRINT_CLONE_ID} table thead {
          display: table-header-group !important;
        }

        body > #${PRINT_CLONE_ID} table tfoot {
          display: table-row-group !important;
        }

        body > #${PRINT_CLONE_ID} .overflow-x-auto,
        body > #${PRINT_CLONE_ID} .overflow-auto {
          overflow: visible !important;
        }

        body > #${PRINT_CLONE_ID} img,
        body > #${PRINT_CLONE_ID} canvas {
          max-width: 100% !important;
          height: auto !important;
          object-fit: contain !important;
        }

        body > #${PRINT_CLONE_ID} img[alt="signature"] {
          display: block !important;
          width: 100% !important;
          min-width: 320px !important;
          min-height: 120px !important;
        }

        body > #${PRINT_CLONE_ID} .task-result-print-signature-image {
          display: block !important;
          width: 100% !important;
          min-width: 320px !important;
          min-height: 120px !important;
          height: auto !important;
        }

        body > #${PRINT_CLONE_ID} .task-result-print-canvas-image {
          display: block !important;
          width: 100% !important;
          min-width: 320px !important;
          height: auto !important;
        }

        body > #${PRINT_CLONE_ID} .signature-pad {
          display: block !important;
          width: 100% !important;
          min-width: 320px !important;
        }
      }
    `;

    document.body.appendChild(clone);
    document.head.appendChild(style);

    let cleanedUp = false;
    const cleanup = () => {
      if (cleanedUp) return;
      cleanedUp = true;
      clone.remove();
      style.remove();
    };

    const waitForImages = Promise.all(
      Array.from(clone.querySelectorAll('img')).map(
        img =>
          new Promise<void>(resolve => {
            const htmlImg = img as HTMLImageElement;

            if (htmlImg.complete && htmlImg.naturalWidth > 0) {
              resolve();
              return;
            }

            const done = () => resolve();
            htmlImg.addEventListener('load', done, { once: true });
            htmlImg.addEventListener('error', done, { once: true });
          }),
      ),
    );

    window.addEventListener('afterprint', cleanup, { once: true });

    void waitForImages.then(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.print();
          setTimeout(cleanup, 1500);
        });
      });
    });
  }, [adaptedQuestionsData, formItems, isResultLoading, result]);

  return (
    <ModalContentWrapper
      variant="right"
      open={open}
      title={
        <FormResultsModalTitle
          disableTitleEditing
          editing={editing}
          onTurnOnEditing={turnOnEditing}
          onPrint={handlePrint}
          showPrint={!isResultLoading && !!result}
          allowPrintForNonAdmin={allowPrintForNonAdmin}
          name={task.name}
          discardButton={{
            onClick: () => {
              formik.setValues(formik.initialValues);
              turnOffEditing();
            },
            disabled: isLoading || isResultLoading || !result,
          }}
          submitButton={{
            onClick: formik.handleSubmit,
            disabled: isLoading || isResultLoading || !result,
          }}
        />
      }
      onClose={onClose}
      classes={{
        dialogPanel: '!max-w-xl',
        children: 'space-y-4 p-0 py-3 sm:py-6',
        // title: 'max-w-[490px]',
        titleWrapper: 'flex-1 [&>span]:w-full mt-[-2px] mr-3',
      }}
    >
      {isResultLoading ? (
        <div className="flex min-h-[320px] items-center justify-center px-3 sm:px-6">
          <Spinner size="lg" variant="primary" />
        </div>
      ) : errorMessage || !result ? (
        <div className="flex min-h-[320px] items-center justify-center px-3 sm:px-6">
          <div>{errorMessage || 'Something went wrong, task result not found'}</div>
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-8 px-3 sm:px-6">
            <div className="flex justify-between">
              <div className="flex flex-col gap-3">
                <div className="text-[12px] text-gray-500">DUE DATE</div>
                <div>{dayjs(task?.first_due_date).format('DD-MM-YYYY, HH:mm')}</div>
              </div>
              <div className="flex flex-col gap-3">
                <div className="text-[12px] text-gray-500">COMPLETED DATE</div>
                <div>{dayjs(result?.completion_date).format('DD-MM-YYYY, HH:mm')}</div>
              </div>
              <div className="flex flex-col gap-3">
                <div className="text-[12px] text-gray-500">ASSIGNEE</div>
                <div className="flex items-center gap-2">
                  <Avatar
                    src={user?.avatar_url}
                    firstName={user?.first_name}
                    lastName={user?.last_name}
                  />
                  <span>
                    {user?.first_name} {user?.last_name}
                  </span>
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <div className="text-xs text-gray-500">RECURRENCE</div>
                <div className="text-sm">
                  {task?.recurrency_rule
                    ? capitalize(RRule.fromString(task?.recurrency_rule).toText())
                    : '-'}
                </div>
              </div>
              <div className="space-y-1.5">
                <span className="text-xs text-gray-500">ID</span>
                <p>{result?.reference_name || '...'}</p>
              </div>
              <div className="space-y-1.5">
                <span className="text-xs text-gray-500">TASK DESCRIPTION</span>
                <p>{task?.description || '...'}</p>
              </div>
            </div>
            {!!task.registers?.length && (
              <div className="flex flex-col gap-3">
                <span className="text-sm font-bold text-gray-600 uppercase">Filters</span>
                <div className="flex flex-col gap-8">
                  <div className="space-y-3">
                    <div className="text-[12px] text-gray-500">LABELS</div>
                    <div className="flex flex-wrap gap-2">
                      {task.registers.map(({ id, name }, index) => (
                        <Badge
                          key={`${id}-${index}`}
                          type={registersFilter.includes(id) ? 'info' : 'neutral'}
                          onClick={editing ? undefined : handleToggleRegisterFilter.bind(null, id)}
                          className={classNames('select-none', {
                            'cursor-pointer': !editing,
                            'opacity-50': editing,
                          })}
                        >
                          {name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  {isUserAdmin && (
                    <div className="space-y-2">
                      <div className="text-[12px] text-gray-500">COMPLIANCE STATUS</div>
                      <Select
                        isClearable
                        disabled={editing}
                        options={complianceOptions}
                        value={complianceFilter}
                        onChange={setComplianceFilter}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="border-b" />
          <div
            className="flex-1 overflow-auto print:bg-white print:text-black print:p-10 px-3 sm:px-6"
            id={'print-area'}
          >
            {result?.completion_date && (
              <div className="print:flex gap-1 hidden text-[16px] leading-6 font-bold font-[Inter]">
                <div>Date completion:</div>
                <div>{dayjs(result?.completion_date).format('DD-MM-YYYY, HH:mm')}</div>
              </div>
            )}
            {!formItems.length && (!!registersFilter.length || complianceFilter) && (
              <div className="w-full h-[150px] flex items-center justify-center">
                <span className="font-semibold text-lg">No items found with this filter</span>
              </div>
            )}
            <Formik
              initialValues={{}}
              onSubmit={() => {
                console.log('Submitted');
              }}
            >
              <ReactFormGenerator
                key={formGeneratorKey}
                hide_actions
                useInitAnswers
                collapseThreeColumnFileUploadLayout={true}
                disabled={!editing}
                editing={editing}
                // readOnly={!editing}
                onChange={handleChangeAnswers}
                onChangeItems={handleChangeItems}
                items={formItems}
                registers={registers}
                isRegistersLoading={isLoadingRegisters || isFetchingRegistersNextPage}
                initAnswers={adaptedQuestionsData}
              />
            </Formik>
          </div>
        </>
      )}
    </ModalContentWrapper>
  );
};

export default TaskResultInfoModal;
